/**
 * How much picture a player may stream.
 *
 * Measured on the short side of the video, so a vertical 720x1280 clip counts
 * as 720p. Every player is capped here by default; see CustomVimeoPlayer's
 * `hlsMaxHeight` for why.
 */
export const VIDEO_MAX_HEIGHT = 720;

const shortSide = (level) => {
  const width = Number(level?.width) || 0;
  const height = Number(level?.height) || 0;
  if (width > 0 && height > 0) return Math.min(width, height);
  return height > 0 ? height : null;
};

/**
 * The hls.js level to use as `autoLevelCapping`: the last level (hls.js sorts
 * them from the smallest up) whose short side fits under `maxShortSide`. When
 * none fits, the smallest level rather than no cap at all. -1 when there is
 * nothing to cap: no cap given, no levels, or no level reports a size.
 */
export function pickCapLevel(levels, maxShortSide) {
  if (!maxShortSide || !Array.isArray(levels) || levels.length === 0) return -1;

  let fitting = -1;
  let smallest = -1;
  let smallestSide = Infinity;

  levels.forEach((level, index) => {
    const side = shortSide(level);
    if (side === null) return;
    if (side <= maxShortSide) fitting = index;
    if (side < smallestSide) {
      smallest = index;
      smallestSide = side;
    }
  });

  return fitting >= 0 ? fitting : smallest;
}

const VIMEO_EMBED_QUALITIES = [240, 360, 540, 720, 1080];

/**
 * The Vimeo embed's `quality` option for a cap: the largest of its fixed steps
 * that fits. It only sets where the embed starts — Vimeo offers no hard cap —
 * and Vimeo ignores it for a video without that rendition.
 */
export function vimeoEmbedQuality(maxShortSide) {
  const fitting = VIMEO_EMBED_QUALITIES.filter((quality) => quality <= maxShortSide);
  return `${fitting.length ? fitting[fitting.length - 1] : VIMEO_EMBED_QUALITIES[0]}p`;
}
